import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DetailRow, DetailSection, DetailValue } from "../detailView";

type OpeningHourLike = {
  id?: string;
  week_day: string;
  from_hour?: string;
  to_hour?: string;
};

interface Address {
  street?: string;
  city?: string;
  zip_code?: string;
  country?: string;
  phone_number?: string;
  email?: string;
  email_counter_reminders?: string;
  notes?: string;
  opening_hours?: OpeningHourLike[];
}

interface AddressDisplayProps {
  address: Address | null | undefined;
  /** Defaults to "Address & Opening Hours". */
  title?: string;
  icon?: React.ReactNode;
  /** Add the address notes and opening hours as rows. */
  withDetails?: boolean;
  /** Add the phone number and emails kept on the address. */
  withContact?: boolean;
  /** Let the header fold the address away. */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  /** Shown instead of the rows when there is no address at all. */
  emptyContent?: React.ReactNode;
}

const buildGoogleMapsUrl = (addr: Address): string => {
  const addressParts = [addr.street, addr.city, addr.zip_code, addr.country]
    .filter(Boolean)
    .join(", ");

  if (!addressParts) {
    return "";
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}`;
};

// Drop the seconds the API sends, e.g. "08:00:00" -> "08:00".
const formatTime = (time?: string) => (time ? time.split(":").slice(0, 2).join(":") : "");

/** An address's weekly opening hours, one line per entry. */
export const OpeningHoursValue = ({ openingHours }: { openingHours?: OpeningHourLike[] | null }) => {
  const { t } = useTranslation();
  const dayNames: Record<string, string> = {
    "1": t("Monday"),
    "2": t("Tuesday"),
    "3": t("Wednesday"),
    "4": t("Thursday"),
    "5": t("Friday"),
    "6": t("Saturday"),
    "7": t("Sunday"),
  };

  if (!Array.isArray(openingHours) || openingHours.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
        {t("No opening hours")}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {openingHours.map((oh, idx) => (
        <DetailValue key={oh.id || idx}>
          {typeof oh === "string"
            ? oh
            : `${dayNames[oh.week_day] || oh.week_day}: ${formatTime(oh.from_hour)} - ${formatTime(oh.to_hour)}`}
        </DetailValue>
      ))}
    </Box>
  );
};

export const AddressDisplay = ({
  address,
  title,
  icon,
  withDetails = false,
  withContact = false,
  collapsible = false,
  defaultExpanded = true,
  emptyContent,
}: AddressDisplayProps) => {
  const { t } = useTranslation();
  const googleMapsUrl = address ? buildGoogleMapsUrl(address) : "";
  const sectionTitle = title ?? `${t("Address")} & ${t("Opening Hours")}`;

  if (!address && emptyContent) {
    return (
      <DetailSection title={sectionTitle} icon={icon} padded>
        {emptyContent}
      </DetailSection>
    );
  }

  return (
    <DetailSection
      title={sectionTitle}
      icon={icon}
      collapsible={collapsible}
      defaultExpanded={defaultExpanded}
      action={
        googleMapsUrl && (
          <Button
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon fontSize="small" />}
          >
            {t("View on Google Maps")}
          </Button>
        )
      }
    >
      <DetailRow label={t("Street")} value={address?.street} />
      <DetailRow label={t("City")} value={address?.city} />
      <DetailRow label={t("Zip Code")} value={address?.zip_code} />
      <DetailRow label={t("Country")} value={address?.country} />
      {withContact && (
        <>
          <DetailRow label={t("Phone Number")} value={address?.phone_number} />
          <DetailRow label={t("Email")} value={address?.email} />
          {address?.email_counter_reminders !== undefined && (
            <DetailRow label={t("Email for counter reminders")} value={address.email_counter_reminders} />
          )}
        </>
      )}
      {withDetails && address?.notes && (
        <DetailRow label={t("Notes")} value={address.notes} multiline />
      )}
      {withDetails && (
        <DetailRow label={t("Opening Hours")} alignTop>
          <OpeningHoursValue openingHours={address?.opening_hours} />
        </DetailRow>
      )}
    </DetailSection>
  );
};
